import { type ReactNode } from "react";
import { type GalleryImageConfig } from "../image/types";
import { extractBoolean } from "./core/extractBoolean";
import { extractGallery } from "./core/extractGallery";
import { extractImage } from "./core/extractImage";
import { extractList } from "./core/extractList";
import { extractMedia } from "./core/extractMedia";
import { extractColors } from "./core/extractColors";
import { extractPlans } from "./core/extractPlans";
import { extractTags } from "./core/extractTags";
import { extractUnit, type ResolvedUnit } from "./core/extractUnit";
import { interpolateNodes } from "./core/interpolateNodes";
import { interpolateString } from "./core/interpolateString";
import { resolveInferredTemplate } from "./internal/resolveInferredTemplate";
import type { TagItem } from "../tags";
import type { PublishedSubscriptionPlan } from "../subscription/types";
import type {
  ExtractedImage,
  ExtractedMedia,
  InterpolateOptions,
  InterpolateInput,
  InterpolateParams,
  ColorItem,
} from "./types";

/** Retorno do `interpolate`. Getters lazy — cada forma só é computada no primeiro acesso. */
export type InterpolateResult = {
  /** String pura. Use em atributos, URLs, SEO e props tipadas `string`. */
  readonly text: string;
  /** Árvore React. Tokens `|datetime` e `|rich_text` viram componentes dedicados. */
  readonly node: ReactNode;
  /** Payload de template single-token `|image`. `undefined` caso contrário. */
  readonly image: ExtractedImage | undefined;
  /** Payload de template single-token `|gallery`. `undefined` caso contrário. */
  readonly gallery: GalleryImageConfig[] | undefined;
  /** Payload estruturado de template single-token `|media`. `undefined` caso contrário. */
  readonly media: ExtractedMedia | undefined;
  /** Valor booleano de um template single-token (`true`/`"true"` → `true`). */
  readonly boolean: boolean;
  /**
   * Lista de strings de templates single-token `|array_string` (ou `|text`
   * apontando para um valor array, como compatibilidade reversa).
   * Sempre retorna `string[]` — `[]` quando o template não case.
   */
  readonly list: string[];
  readonly colors: ColorItem[] | undefined;
  readonly plans: PublishedSubscriptionPlan[];
  readonly tags: TagItem[];
  /**
   * Unidade do canal apontada por um template single-token `|unit_id`.
   * `undefined` quando o template não casa ou o id não pertence ao canal.
   */
  readonly unit: ResolvedUnit | undefined;
};

/**
 * Interpola um template `{{path|type}}` contra `structure` e devolve um
 * objeto com formas (`text`/`node`/`image`/`gallery`) sob demanda.
 *
 * Em vez de `template`, pode-se passar `infer: { type, context }` para
 * descobrir o campo automaticamente via `_metadata` (sem template manual).
 *
 * @example
 * ```tsx
 * interpolate({ structure, template: title }).node
 * interpolate({ structure, template: body }, { parseHtml: true }).node
 * interpolate({ structure, infer: { type: "colors", context: "item" } }).colors
 * ```
 */
function normalizeInterpolateInput(input: InterpolateInput): InterpolateParams {
  if ("infer" in input && input.infer) {
    return {
      structure: input.structure,
      template: resolveInferredTemplate(input.structure, input.infer) ?? "",
    };
  }

  return {
    structure: input.structure,
    template: "template" in input ? (input.template ?? "") : "",
  };
}

export function interpolate(
  input: InterpolateInput,
  options?: InterpolateOptions,
): InterpolateResult {
  const params = normalizeInterpolateInput(input);

  return {
    get text() {
      return interpolateString(params, options);
    },
    get node() {
      return interpolateNodes(params, options);
    },
    get image() {
      return extractImage(params);
    },
    get gallery() {
      return extractGallery(params);
    },
    get media() {
      return extractMedia(params);
    },
    get boolean() {
      return extractBoolean(params);
    },
    get list() {
      return extractList(params);
    },
    get colors() {
      return extractColors(params);
    },
    get plans() {
      return extractPlans(params);
    },
    get tags() {
      return extractTags(params);
    },
    get unit() {
      return extractUnit(params);
    },
  };
}
